/**
 * The AZmed release version, as carried by frontend/package.json: the ECR image
 * tag and the README changelog both track it.
 *
 * Injected at build time by the RELEASE_VERSION entry of the DefinePlugin in
 * frontend/.webpack/webpack.base.js. The full `process.env.RELEASE_VERSION`
 * path must stay written out below: DefinePlugin substitutes text, so
 * destructuring process.env would silently yield undefined in the browser.
 *
 * Beware: this module only works inside the viewer build. The platform/ui
 * package has its own webpack config, without that DefinePlugin entry.
 */

/**
 * @returns {string} The release version, empty when the build did not inject it
 */
export const getReleaseVersion = () =>
  String(process.env.RELEASE_VERSION || '').trim();

/**
 * Reduces a version to its feature series, dropping the patch level and any
 * pre-release suffix. Both parts must be numeric, so anything unexpected
 * returns empty rather than a series that could match by accident.
 * @param {string} version A version, ie. '4.4.1' or '4.4.0-rc.1'
 * @returns {string} Its major.minor series, ie. '4.4', empty when unparseable
 */
const getFeatureSeries = version => {
  const [major, minor] = String(version || '')
    .trim()
    .split('.');

  if (!/^\d+$/.test(major || '') || !/^\d+$/.test(minor || '')) return '';

  return `${major}.${minor}`;
};

/**
 * Compares a version against the running release, on the major.minor series
 * only: patch releases keep a feature marked as new, the next minor drops it.
 * No semantic ordering is involved, so a downgrade behaves like any mismatch.
 * An unparseable version on either side never matches, which is why a build
 * without RELEASE_VERSION hides rather than shows.
 * @param {string} version A version to test, ie. '4.4.0'
 * @returns {boolean} Whether it belongs to the running feature series
 */
export const isCurrentFeatureSeries = version => {
  const target = getFeatureSeries(version);

  return Boolean(target) && getFeatureSeries(getReleaseVersion()) === target;
};
